import {
    Download as DownloadIcon,
    MenuBook as RegistruIcon,
    Print as PrintIcon,
    Search as SearchIcon,
} from '@mui/icons-material';
import { Alert, Box, Button, Checkbox, FormControlLabel, Paper, TextField } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import dayjs from 'dayjs';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PageHeaderBanner from '../components/PageHeaderBanner';
import { createRegistruCsv, RegistruRow } from '../domain/registru';
import { useDeferredQuery } from '../hooks/useQuery';

const Registru = () => {
    const { t } = useTranslation();
    const today = dayjs().format('YYYY-MM-DD');
    const [from, setFrom] = useState(today);
    const [to, setTo] = useState(today);
    const [oneDayOnly, setOneDayOnly] = useState(false);
    const [query, setQuery] = useState<{ from: string; to: string; oneDayOnly: boolean } | null>(null);
    const { data = [], loading, error } = useDeferredQuery<RegistruRow[]>({
        path: '/registru',
        params: query ?? {},
        shouldExecute: query !== null,
    });

    const columns = useMemo<GridColDef<RegistruRow>[]>(
        () => [
            { field: 'status', headerName: t('registru.columns.status'), width: 90 },
            { field: 'contractNumber', headerName: t('registru.columns.contractNumber'), width: 130 },
            { field: 'numeClient', headerName: t('registru.columns.client'), width: 190 },
            { field: 'pawnedItemDescription', headerName: t('registru.columns.item'), width: 190 },
            { field: 'unitOfMeasure', headerName: t('registru.columns.unit'), width: 80 },
            { field: 'quantity', headerName: t('registru.columns.quantity'), width: 100 },
            { field: 'loanAmountEur', headerName: t('registru.columns.amountEur'), width: 110 },
            { field: 'exchangeRate', headerName: t('registru.columns.exchangeRate'), width: 100 },
            { field: 'loanAmount', headerName: t('registru.columns.amountRon'), width: 110 },
            { field: 'dailyInterestRate', headerName: t('registru.columns.dailyInterest'), width: 120 },
            { field: 'originalDueDate', headerName: t('registru.columns.dueDate'), width: 125 },
            { field: 'redemptionDate', headerName: t('registru.columns.redemptionDate'), width: 125 },
            { field: 'redemptionAmountRon', headerName: t('registru.columns.redemptionAmount'), width: 140 },
            { field: 'expirationDate', headerName: t('registru.columns.expirationDate'), width: 125 },
            { field: 'contractDate', headerName: t('registru.columns.contractDate'), width: 125 },
            { field: 'durationDays', headerName: t('registru.columns.duration'), width: 90, type: 'number' },
            { field: 'staffCode', headerName: t('registru.columns.staff'), width: 100 },
            { field: 'contractType', headerName: t('registru.columns.type'), width: 110 },
        ],
        [t],
    );

    const exportCsv = () => {
        const blob = new Blob(['\uFEFF', createRegistruCsv(data)], { type: 'text/csv;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `registru-${query?.from ?? from}-${query?.to ?? to}.csv`;
        link.click();
        URL.revokeObjectURL(url);
    };

    return (
        <Box sx={{ p: 2, height: 'calc(100vh - 64px)', display: 'flex', flexDirection: 'column', gap: 2 }}>
            <PageHeaderBanner
                icon={<RegistruIcon />}
                title={t('registru.title')}
                subtitle={t('registru.subtitle')}
                accentColor="#0ea5e9"
            />
            <Paper sx={{ p: 2, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                <TextField label={t('registru.from')} type="date" value={from} onChange={event => setFrom(event.target.value)} slotProps={{ inputLabel: { shrink: true } }} />
                <TextField label={t('registru.to')} type="date" value={to} onChange={event => setTo(event.target.value)} slotProps={{ inputLabel: { shrink: true } }} />
                <FormControlLabel control={<Checkbox checked={oneDayOnly} onChange={event => setOneDayOnly(event.target.checked)} />} label={t('registru.oneDayOnly')} />
                <Button variant="contained" startIcon={<SearchIcon />} disabled={!from || !to || from > to} onClick={() => setQuery({ from, to, oneDayOnly })}>{t('registru.show')}</Button>
                <Button startIcon={<PrintIcon />} disabled={data.length === 0} onClick={() => window.print()}>{t('registru.print')}</Button>
                <Button startIcon={<DownloadIcon />} disabled={data.length === 0} onClick={exportCsv}>{t('registru.export')}</Button>
            </Paper>
            {error && <Alert severity="error">{t('registru.loadError')}</Alert>}
            <Paper sx={{ flex: 1, minHeight: 0 }}>
                <DataGrid rows={data} columns={columns} getRowId={row => row.nr} loading={loading} disableRowSelectionOnClick showToolbar initialState={{ pagination: { paginationModel: { pageSize: 25 } } }} pageSizeOptions={[25, 50, 100]} />
            </Paper>
        </Box>
    );
};

export default Registru;
